"use client";

import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { useRequireAuth } from "@/hooks/useRequireAuth";
import {
  parseCSVRaw,
  buildMappedRows,
  CANONICAL_FIELDS,
  autoMapCsvHeaders,
} from "@/lib/csv";
import { parsePDFToRows } from "@/lib/pdf";
import { normalizeRowsToProducts } from "@/lib/normalize";
import { setInStorage } from "@/lib/storage";
import { getAuthHeaders, getUploadAuthHeaders } from "@/lib/auth-client";
import { STORAGE_KEYS } from "@/lib/constants";
import { DEFAULT_SETTINGS, FONT_OPTIONS, FONT_SIZE_OPTIONS } from "@/types/product";
import { DEMO_PRODUCTS } from "@/lib/demoProducts";
import { validateSlug, normalizeSlug } from "@/lib/slug";
import { useStorefrontAuthOptional } from "@/context/StorefrontAuthContext";
import { TemplatePicker } from "@/components/TemplatePicker";
import type { TemplateId } from "@/lib/templates";
import type { Product, StorefrontSettings } from "@/types/product";

type Step = "template" | "upload" | "mapping" | "preview" | "slug" | "success";

const REQUIRED_FIELDS = ["title", "price"];
const RENTAL_REQUIRED_FIELDS = ["title", "price", "inventory"];

function getDefaultMapping(rawHeaders: string[]): Record<string, number> {
  return autoMapCsvHeaders(rawHeaders);
}

const BASE_DOMAIN =
  typeof window !== "undefined"
    ? process.env.NEXT_PUBLIC_APP_DOMAIN || window.location.hostname
    : "localhost";

export default function UploadPage() {
  const { ready } = useRequireAuth();
  const [step, setStep] = useState<Step>("template");
  const [isRentalMode, setIsRentalMode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rawHeaders, setRawHeaders] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<Record<string, number>>({});
  const [fileName, setFileName] = useState<string>("");
  const [confirmedProducts, setConfirmedProducts] = useState<Product[]>([]);
  const [confirmedSettings, setConfirmedSettings] = useState<StorefrontSettings>(DEFAULT_SETTINGS);
  const [slugInput, setSlugInput] = useState("");
  const [storeNameInput, setStoreNameInput] = useState("");
  const [primaryColorInput, setPrimaryColorInput] = useState(DEFAULT_SETTINGS.primaryColor);
  const [currencyDefaultInput, setCurrencyDefaultInput] = useState(DEFAULT_SETTINGS.currencyDefault);
  const [fontInput, setFontInput] = useState(DEFAULT_SETTINGS.font ?? "sans");
  const [fontSizeInput, setFontSizeInput] = useState<"sm" | "md" | "lg" | "xl">(DEFAULT_SETTINGS.fontSize ?? "md");
  const [backgroundColorInput, setBackgroundColorInput] = useState(DEFAULT_SETTINGS.backgroundColor ?? "#ffffff");
  const [foregroundColorInput, setForegroundColorInput] = useState(DEFAULT_SETTINGS.foregroundColor ?? "#1e293b");
  const [headerBackgroundColorInput, setHeaderBackgroundColorInput] = useState(DEFAULT_SETTINGS.headerBackgroundColor ?? "#0f172a");
  const [selectedTemplateId, setSelectedTemplateId] = useState<TemplateId | null>(null);
  const [createdStoreUrl, setCreatedStoreUrl] = useState("");
  const [slugChecking, setSlugChecking] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [slugCheckError, setSlugCheckError] = useState<string | null>(null);
  const [parsingPdf, setParsingPdf] = useState(false);
  const auth = useStorefrontAuthOptional();

  // Image generation state
  const [imageGenRunning, setImageGenRunning] = useState(false);
  const [imageGenErrors, setImageGenErrors]   = useState<string[]>([]);
  const [imageGenDone, setImageGenDone]       = useState(false);

  // Preload all Google Fonts so the font picker renders each name in its own typeface
  useEffect(() => {
    FONT_OPTIONS.forEach((f) => {
      if (!f.googleFont) return;
      const id = `gfont-${f.value}`;
      if (document.getElementById(id)) return;
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href = `https://fonts.googleapis.com/css2?family=${f.googleFont}:wght@400;600;700&display=swap`;
      document.head.appendChild(link);
    });
  }, []);

  const mappedRows = rawRows.length
    ? buildMappedRows(rawRows, mapping)
    : [];
  const previewRows = mappedRows.slice(0, 10);
  const activeRequiredFields = isRentalMode ? RENTAL_REQUIRED_FIELDS : REQUIRED_FIELDS;
  const missingRequired = activeRequiredFields.filter((f) => mapping[f] === undefined || mapping[f] < 0);

  const handleFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setError(null);
      const file = e.target.files?.[0];
      if (!file) return;
      const isCsv = file.name.toLowerCase().endsWith(".csv");
      const isPdf = file.name.toLowerCase().endsWith(".pdf");
      if (!isCsv && !isPdf) {
        setError("Please upload a CSV or PDF file.");
        return;
      }
      setFileName(file.name);

      if (isCsv) {
        const reader = new FileReader();
        reader.onload = () => {
          const text = String(reader.result ?? "");
          const raw = parseCSVRaw(text);
          if (!raw.ok) {
            setError(raw.error);
            return;
          }
          setRawHeaders(raw.rawHeaders);
          setRawRows(raw.rawRows);
          const defaultMapping = getDefaultMapping(raw.rawHeaders);
          setMapping(defaultMapping);
          const hasTitle = defaultMapping.title !== undefined && defaultMapping.title >= 0;
          const hasPrice = defaultMapping.price !== undefined && defaultMapping.price >= 0;
          if (hasTitle && hasPrice && raw.rawHeaders.length > 0) {
            setStep("preview");
          } else {
            setStep("mapping");
          }
        };
        reader.readAsText(file, "UTF-8");
        return;
      }

      if (isPdf) {
        setParsingPdf(true);
        file.arrayBuffer()
          .then((buffer) => parsePDFToRows(buffer))
          .then((raw) => {
            setParsingPdf(false);
            if (!raw.ok) {
              setError(raw.error);
              return;
            }
            setRawHeaders(raw.rawHeaders);
            setRawRows(raw.rawRows);
            const defaultMapping = getDefaultMapping(raw.rawHeaders);
            setMapping(defaultMapping);
            const hasTitle = defaultMapping.title !== undefined && defaultMapping.title >= 0;
            const hasPrice = defaultMapping.price !== undefined && defaultMapping.price >= 0;
            if (hasTitle && hasPrice && raw.rawHeaders.length > 0) {
              setStep("preview");
            } else {
              setStep("mapping");
            }
          })
          .catch(() => {
            setParsingPdf(false);
            setError("Failed to read or parse the PDF.");
          });
      }
    },
    []
  );

  const needMapping = missingRequired.length > 0 || step === "mapping";

  const goToPreview = () => {
    if (missingRequired.length > 0) {
      setError(`Map required columns: ${missingRequired.join(", ")}`);
      return;
    }
    setError(null);
    setStep("preview");
  };

  const handleConfirmPreview = () => {
    const norm = normalizeRowsToProducts(
      mappedRows.map((r) => ({ ...r } as Record<string, string | number | undefined>))
    );
    if (!norm.ok) {
      setError(`Row ${norm.error.rowIndex}: ${norm.error.message}`);
      return;
    }
    setConfirmedProducts(norm.products);
    // Preserve template settings (including isRental) — only fill gaps with DEFAULT_SETTINGS
    setConfirmedSettings((prev) => ({ ...DEFAULT_SETTINGS, ...prev }));
    setStoreNameInput("My Store");
    setError(null);
    setImageGenRunning(false);
    setImageGenDone(false);
    setImageGenErrors([]);
    setStep("slug");
  };

  const startImageGeneration = useCallback(async () => {
    setImageGenRunning(true);
    setImageGenErrors([]);
    // getUploadAuthHeaders sends JWT as Bearer OR api key as X-API-Key (not Bearer),
    // which is what FastAPI's get_current_user_flexible expects.
    const authHeaders = getUploadAuthHeaders();
    const errors: string[] = [];
    const updated = [...confirmedProducts];
    for (let i = 0; i < updated.length; i++) {
      const p = updated[i];
      if (p.imageUrl?.trim()) continue;
      try {
        // Call FastAPI directly through /api/proxy/ (nginx → Next.js rewrite → FastAPI).
        // This avoids the /api/ nginx rule that would bypass the Next.js proxy route.
        let prompt = `professional product photography of ${p.title}`;
        if (p.description?.trim()) prompt += `, ${p.description.trim().slice(0, 150)}`;
        prompt += ", white background, studio lighting, high detail, commercial quality";
        const res = await fetch("/api/proxy/generate-product-image/", {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders },
          body: JSON.stringify({
            product_name: p.title,
            prompt,
            model: "sdxl-turbo",
            save_to_qdrant: false,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.url) updated[i] = { ...p, imageUrl: data.url };
        else errors.push(`"${p.title}": ${data.detail || data.error || `HTTP ${res.status}`}`);
      } catch {
        errors.push(`"${p.title}": network error`);
      }
    }
    setConfirmedProducts(updated);
    setImageGenErrors(errors);
    setImageGenRunning(false);
    setImageGenDone(true);
  }, [confirmedProducts]);

  const handleTemplateSelect = (partial: Partial<StorefrontSettings>, id: TemplateId, rental: boolean) => {
    if (partial.primaryColor)          setPrimaryColorInput(partial.primaryColor);
    if (partial.backgroundColor)       setBackgroundColorInput(partial.backgroundColor);
    if (partial.headerBackgroundColor) setHeaderBackgroundColorInput(partial.headerBackgroundColor);
    if (partial.foregroundColor)       setForegroundColorInput(partial.foregroundColor);
    if (partial.font)                  setFontInput(partial.font);
    setConfirmedSettings((prev) => ({ ...prev, ...partial }));
    setSelectedTemplateId(id);
    setIsRentalMode(rental);
    setStep("upload");
  };

  const checkSlugAvailability = useCallback(async (slug: string) => {
    const normalized = normalizeSlug(slug);
    setSlugCheckError(null);
    if (!normalized) {
      setSlugAvailable(null);
      return;
    }
    const validation = validateSlug(normalized);
    if (!validation.valid) {
      setSlugAvailable(false);
      setSlugCheckError(validation.error);
      return;
    }
    setSlugChecking(true);
    setSlugAvailable(null);
    try {
      const res = await fetch(`/api/stores?slug=${encodeURIComponent(normalized)}`);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSlugAvailable(data.exists === false);
        setSlugCheckError(null);
      } else {
        setSlugAvailable(null);
        setSlugCheckError(
          res.status >= 500
            ? "Could not check availability. Add SUPABASE_SERVICE_ROLE_KEY to .env.local (Project Settings → API in Supabase)."
            : (data.error || "Could not check availability.")
        );
      }
    } catch {
      setSlugAvailable(null);
      setSlugCheckError("Network error. Could not check availability.");
    } finally {
      setSlugChecking(false);
    }
  }, []);

  const handleCreateStore = async () => {
    const normalized = normalizeSlug(slugInput.trim());
    const validation = validateSlug(slugInput.trim());
    if (!validation.valid) {
      setError(validation.error);
      return;
    }
    setError(null);
    const name = storeNameInput.trim() || "My Store";
    setSlugChecking(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/stores", {
        method: "POST",
        headers,
        body: JSON.stringify({
          slug: normalized,
          name,
          settings: {
            ...confirmedSettings,
            // Per-input overrides win over template defaults
            primaryColor: primaryColorInput,
            currencyDefault: currencyDefaultInput,
            font: fontInput,
            fontSize: fontSizeInput,
            backgroundColor: backgroundColorInput,
            headerBackgroundColor: headerBackgroundColorInput,
            foregroundColor: foregroundColorInput,
          },
          products: confirmedProducts,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 401) {
          setError("Please sign in to publish your store.");
          return;
        }
        setError(data.error || "Failed to create store");
        return;
      }
      const origin = typeof window !== "undefined"
        ? window.location.origin
        : process.env.NEXT_PUBLIC_APP_ORIGIN || "http://localhost:3000";
      const storeUrl = `${origin}/s/${normalized}`;
      setCreatedStoreUrl(storeUrl);
      setInStorage(STORAGE_KEYS.PRODUCTS, confirmedProducts);
      setInStorage(STORAGE_KEYS.SETTINGS, {
        ...confirmedSettings,
        primaryColor: primaryColorInput,
        currencyDefault: currencyDefaultInput,
        font: fontInput,
        fontSize: fontSizeInput,
        backgroundColor: backgroundColorInput,
        headerBackgroundColor: headerBackgroundColorInput,
        foregroundColor: foregroundColorInput,
      });
      setStep("success");
      if (typeof window !== "undefined") {
        window.open(storeUrl, "_blank");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSlugChecking(false);
    }
  };

  const loadDemoData = () => {
    setInStorage(STORAGE_KEYS.PRODUCTS, DEMO_PRODUCTS);
    setInStorage(STORAGE_KEYS.SETTINGS, DEFAULT_SETTINGS);
    window.location.href = "/store";
  };

  const resetDemo = () => {
    if (typeof window !== "undefined" && confirm("Replace current catalog with demo products?")) {
      loadDemoData();
    }
  };

  const cardClass = "upload-card rounded-2xl border border-border bg-surface p-8 shadow-sm";
  const btnPrimary = "btn-primary rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow transition hover:bg-primary-650";
  const btnSecondary = "btn-secondary rounded-xl border border-border bg-surface px-5 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-100";
  const inputClass = "rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20";

  if (!ready) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <p className="text-slate-500">Loading…</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl">
      <h1 className="mb-2 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
        Create storefront
      </h1>
      <p className="mb-8 text-slate-500">
        Choose a template, then import products from a CSV or PDF file.
      </p>

      {step === "template" && (
        <TemplatePicker
          onSelect={handleTemplateSelect}
          defaultSelectedId={selectedTemplateId}
        />
      )}

      {step === "upload" && (
        <div className={cardClass}>
          {isRentalMode && (
            <div className="mb-4 flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-800">
              <span className="font-semibold">Rental mode enabled.</span>
              <span>Your CSV must include an <strong>inventory</strong> column — this sets how many units are available for rent at any given time.</span>
            </div>
          )}
          <p className="leading-relaxed text-slate-600">
            Upload a CSV or PDF with product data. Headers can be: id, sku, title/name,
            description/desc, price, currency, category, image/imageUrl,
            inventory/stock, tags. PDFs are converted to text; table structure works best.
          </p>
          <label className="mt-6 flex cursor-pointer flex-col items-start gap-2">
            <span className="rounded-xl border border-border bg-surface-muted px-5 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-200">
              {parsingPdf ? "Parsing PDF…" : "Choose CSV or PDF file"}
            </span>
            <input
              type="file"
              accept=".csv,.pdf"
              onChange={handleFile}
              className="hidden"
              disabled={parsingPdf}
            />
          </label>
          <div className="mt-6 flex gap-3">
            <button type="button" onClick={() => setStep("template")} className={btnSecondary}>
              Back
            </button>
          </div>
          <div className="mt-6 border-t border-border pt-6">
            <p className="mb-2 text-sm font-medium text-slate-600">
              Just want to try it?
            </p>
            <button
              type="button"
              onClick={loadDemoData}
              className="rounded-xl border border-border bg-surface-muted px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-200"
            >
              Load demo catalog
            </button>
          </div>
        </div>
      )}

      {step === "mapping" && (
        <div className={cardClass}>
          <p className="mb-6 text-slate-600">
            Map your CSV columns to product fields. Required:{" "}
            <strong>title</strong>, <strong>price</strong>
            {isRentalMode && <>, <strong className="text-red-600">inventory</strong> (rental)</>}.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Expected field
                  </th>
                  <th className="py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    CSV column
                  </th>
                </tr>
              </thead>
              <tbody>
                {CANONICAL_FIELDS.map((field) => (
                  <tr key={field} className="border-b border-border/60">
                    <td className="py-3 font-medium text-slate-800">
                      {field}
                      {activeRequiredFields.includes(field) && (
                        <span className="ml-1 text-red-500">*</span>
                      )}
                    </td>
                    <td className="py-3">
                      <select
                        value={mapping[field] ?? ""}
                        onChange={(e) =>
                          setMapping((m) => ({
                            ...m,
                            [field]: parseInt(e.target.value, 10),
                          }))
                        }
                        className={inputClass}
                      >
                        <option value="">— Skip —</option>
                        {rawHeaders.map((h, i) => (
                          <option key={i} value={i}>
                            {h || `Column ${i + 1}`}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-8 flex gap-3">
            <button type="button" onClick={() => setStep("upload")} className={btnSecondary}>
              Back
            </button>
            <button type="button" onClick={goToPreview} className={btnPrimary}>
              Next: preview
            </button>
          </div>
        </div>
      )}

      {step === "preview" && (
        <div className={cardClass}>
          <p className="mb-6 text-slate-600">
            First 10 rows. Next, choose your store URL and publish.
          </p>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface-muted/50">
                  {Object.keys(mapping).filter((k) => mapping[k] >= 0).map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, i) => (
                  <tr key={i} className="border-b border-border/60 last:border-0">
                    {Object.keys(mapping).filter((k) => mapping[k] >= 0).map((h) => (
                      <td key={h} className="max-w-[200px] truncate px-4 py-3 text-slate-700">
                        {row[h] ?? "—"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-slate-500">
            Showing 10 of {mappedRows.length} rows.
          </p>
          <div className="mt-8 flex gap-3">
            <button
              type="button"
              onClick={() => setStep(rawHeaders.length ? "mapping" : "upload")}
              className={btnSecondary}
            >
              Back
            </button>
            <button type="button" onClick={handleConfirmPreview} className={btnPrimary}>
              Next: store details
            </button>
          </div>
        </div>
      )}

      {step === "slug" && (
        <div className={cardClass}>
          {!auth?.user && (
            <p className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <Link href="/login?mode=storefront" className="font-medium underline">Sign in</Link>
              {" "}to publish your store and save it to your account.
            </p>
          )}
          <p className="mb-6 text-slate-600">
            Choose your store URL. It will be available as a path (e.g. <strong>myshop</strong> → /s/myshop).
          </p>
          <div className="space-y-4">
            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Store name
              </span>
              <input
                type="text"
                value={storeNameInput}
                onChange={(e) => setStoreNameInput(e.target.value)}
                placeholder="My Store"
                className={inputClass + " w-full"}
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Store URL slug
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-slate-500">/s/</span>
                <input
                  type="text"
                  value={slugInput}
                  onChange={(e) => {
                    setSlugInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""));
                    setSlugAvailable(null);
                    setSlugCheckError(null);
                  }}
                  onBlur={() => slugInput.trim() && checkSlugAvailability(slugInput.trim())}
                  placeholder="my-store"
                  className={inputClass + " min-w-[12rem]"}
                />
              </div>
              {slugChecking && (
                <p className="mt-1 text-xs text-slate-500">Checking…</p>
              )}
              {slugCheckError && slugInput.trim() && !slugChecking && (
                <p className="mt-1 text-xs text-amber-600">{slugCheckError}</p>
              )}
              {!slugCheckError && slugAvailable === false && slugInput.trim() && !slugChecking && (
                <p className="mt-1 text-xs text-amber-600">This URL is already in use.</p>
              )}
              {slugAvailable === true && !slugCheckError && (
                <p className="mt-1 text-xs text-emerald-600">Available.</p>
              )}
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Primary color
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="color"
                  value={primaryColorInput}
                  onChange={(e) => setPrimaryColorInput(e.target.value)}
                  className="h-10 w-14 cursor-pointer rounded border border-border"
                />
                <input
                  type="text"
                  value={primaryColorInput}
                  onChange={(e) => setPrimaryColorInput(e.target.value)}
                  className={inputClass + " w-24 font-mono text-sm"}
                />
              </div>
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Header color
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="color"
                  value={headerBackgroundColorInput}
                  onChange={(e) => setHeaderBackgroundColorInput(e.target.value)}
                  className="h-10 w-14 cursor-pointer rounded border border-border"
                />
                <input
                  type="text"
                  value={headerBackgroundColorInput}
                  onChange={(e) => setHeaderBackgroundColorInput(e.target.value)}
                  className={inputClass + " w-24 font-mono text-sm"}
                />
              </div>
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Default currency
              </span>
              <select
                value={currencyDefaultInput}
                onChange={(e) => setCurrencyDefaultInput(e.target.value)}
                className={inputClass + " w-full max-w-[12rem]"}
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="INR">INR</option>
                <option value="CAD">CAD</option>
                <option value="AUD">AUD</option>
              </select>
            </label>
            <div className="block">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Font
              </span>
              <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
                {FONT_OPTIONS.map((f) => {
                  const active = fontInput === f.value;
                  return (
                    <button
                      key={f.value}
                      type="button"
                      onClick={() => setFontInput(f.value)}
                      className="rounded-lg border-2 px-2.5 py-2 text-sm leading-tight text-left transition-all"
                      style={{
                        fontFamily: f.fontFamily,
                        borderColor: active ? "#0f172a" : "#e2e8f0",
                        backgroundColor: active ? "#f8fafc" : "#ffffff",
                        color: active ? "#0f172a" : "#64748b",
                      }}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="block">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Font Size
              </span>
              <div className="flex gap-1.5">
                {FONT_SIZE_OPTIONS.map((f) => {
                  const active = fontSizeInput === f.value;
                  return (
                    <button
                      key={f.value}
                      type="button"
                      onClick={() => setFontSizeInput(f.value)}
                      className="flex-1 rounded-lg border-2 py-2 text-xs font-semibold transition-all"
                      style={{
                        borderColor: active ? "#0f172a" : "#e2e8f0",
                        backgroundColor: active ? "#f8fafc" : "#ffffff",
                        color: active ? "#0f172a" : "#64748b",
                        fontSize: `${f.px * 0.65}px`,
                      }}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Background color
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="color"
                  value={backgroundColorInput}
                  onChange={(e) => setBackgroundColorInput(e.target.value)}
                  className="h-10 w-14 cursor-pointer rounded border border-border"
                />
                <input
                  type="text"
                  value={backgroundColorInput}
                  onChange={(e) => setBackgroundColorInput(e.target.value)}
                  className={inputClass + " w-24 font-mono text-sm"}
                />
              </div>
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Text color
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="color"
                  value={foregroundColorInput}
                  onChange={(e) => setForegroundColorInput(e.target.value)}
                  className="h-10 w-14 cursor-pointer rounded border border-border"
                />
                <input
                  type="text"
                  value={foregroundColorInput}
                  onChange={(e) => setForegroundColorInput(e.target.value)}
                  className={inputClass + " w-24 font-mono text-sm"}
                />
              </div>
            </label>
          </div>
          {/* Premium image generation */}
          {(() => {
            const needsImages = confirmedProducts.filter(p => !p.imageUrl?.trim()).length;
            if (needsImages === 0 && !imageGenDone) return null;
            return (
              <div className="mt-6 rounded-xl border border-border bg-surface-muted p-4">
                <p className="mb-3 text-sm font-medium text-slate-700">
                  {imageGenDone
                    ? imageGenErrors.length > 0
                      ? `⚠ ${confirmedProducts.filter(p => p.imageUrl?.trim()).length} images generated, ${imageGenErrors.length} failed`
                      : `✓ AI images generated for all products`
                    : `${needsImages} product${needsImages !== 1 ? "s" : ""} have no image`}
                </p>
                {imageGenErrors.length > 0 && (
                  <ul className="mb-3 list-inside list-disc text-xs text-amber-700">
                    {imageGenErrors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                )}
                {!imageGenDone && (
                  <button
                    type="button"
                    onClick={startImageGeneration}
                    disabled={imageGenRunning}
                    className={btnPrimary + " flex items-center gap-2"}
                  >
                    {imageGenRunning ? (
                      <>
                        <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Generating images…
                      </>
                    ) : (
                      `✨ Generate premium images`
                    )}
                  </button>
                )}
              </div>
            );
          })()}

          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={() => setStep("preview")}
              className={btnSecondary}
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleCreateStore}
              disabled={
                slugChecking ||
                imageGenRunning ||
                !slugInput.trim() ||
                slugAvailable === false ||
                !auth?.user
              }
              className={btnPrimary}
            >
              {slugChecking ? "Publishing…" : auth?.user ? "Publish storefront" : "Sign in to publish"}
            </button>
          </div>
        </div>
      )}

      {step === "success" && (
        <div className={cardClass}>
          <h2 className="text-xl font-semibold text-slate-900">Your store is live</h2>
          <p className="mt-2 text-slate-600">
            Share your storefront or open it to browse.
          </p>
          <p className="mt-4">
            <a
              href={createdStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-primary-600 hover:underline"
            >
              {createdStoreUrl}
            </a>
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={createdStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={btnPrimary}
            >
              Open storefront
            </a>
            <a href="/dashboard" className={btnSecondary}>
              Back to dashboard
            </a>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700">
          {error}
        </div>
      )}

      <div className="mt-8 border-t border-border pt-6">
        <p className="text-sm text-slate-500">
          Reset to demo data (replaces current catalog):{" "}
          <button
            type="button"
            onClick={resetDemo}
            className="font-medium text-primary-600 transition hover:text-primary-700"
          >
            Reset demo data
          </button>
        </p>
      </div>
    </div>
  );
}
